<template>
  <!-- Mobile: Direct BetSlip rendering -->
  <div v-if="isMobile && isOpen" class="mobile-betslip-panel">
    <div class="mobile-betslip-header">
      <h3 class="mobile-betslip-title">BetSlip</h3>
      <button class="mobile-close-button" @click="close">×</button>
    </div>
    <div class="mobile-betslip-content">
      <BetSlip @close="close" />
    </div>
  </div>

  <!-- Desktop: Modal overlay -->
  <transition name="modal-slide-down">
    <div v-if="!isMobile && isOpen" class="desktop-modal-overlay" @click.self="close">
      <div class="desktop-modal-box">
        <div class="desktop-modal-header">
          <h3 class="desktop-modal-title">BetSlip</h3>
          <button class="desktop-close-button" @click="close">×</button>
        </div>
        <div class="desktop-modal-content">
          <BetSlip @close="close" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import BetSlip from '@/components/BetSlip.vue'
import { iframeCommunication } from '@/utils/iframeCommunication';
import { ref, onMounted, onUnmounted, watch, defineProps, defineEmits } from 'vue'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches
}

const isInIframe = iframeCommunication.isRunningInIframe()

// Prevent body scrolling when mobile panel is open
const preventBodyScroll = () => {
  if (isInIframe) return; // Don't lock scroll in iframe mode
  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.width = '100%'
}

const restoreBodyScroll = () => {
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
}

// Watch for mobile panel open/close
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && isInIframe) {
    iframeCommunication.sendScrollToTop()
  }
  
  if (isMobile.value && isOpen) {
    preventBodyScroll()
  } else if (isMobile.value && !isOpen) {
    restoreBodyScroll()
  }
})

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  restoreBodyScroll()
})

const close = () => emit('close')
</script>

<style scoped>
/* Mobile slide-up panel styles */
.mobile-betslip-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  background-color: var(--lottery-card-gradient);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  transform: translateY(0);
  transition: transform 0.3s ease-in-out;
  color: var(--text-white);  
  padding-bottom: env(safe-area-inset-bottom);
}

.mobile-betslip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color, #333);
  flex-shrink: 0;
  background-color: var(--lottery-card-gradient);
  position: sticky;
  top: 0;
  z-index: 1;
  padding-top: calc(16px + env(safe-area-inset-top));
}

.mobile-betslip-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
}

.mobile-close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: var(--text-light);
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mobile-close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.mobile-betslip-content {
  flex: 1;
  overflow-y: auto;
  /* padding: 8px 20px 20px 20px; */
  height: 0;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  /* Ensure content doesn't get cut off */
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
}

/* Desktop modal styles */
.desktop-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--shadow-color);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 8px;
  overflow-y: auto;
}

.desktop-modal-box {
  background-color: var(--bg-table-header, var(--lottery-card-gradient));
  width: 100%;
  max-width: 1280px;
  padding: 8px;
  position: relative;
  color: var(--text-white);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
  margin: 0 auto;
  box-sizing: border-box;
}

.desktop-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.desktop-modal-title {
  margin: 0;
  font-size: 18px;
}

.desktop-close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-light);
  cursor: pointer;
}

.desktop-modal-content {
  padding: 8px;
}

/* Responsive behavior */
@media (max-width: 768px) {
  .desktop-modal-overlay {
    display: none;
    padding: 0;
  }
  
  .desktop-modal-box {
    padding: 0;
    max-width: 100%;
    margin: 0;
  }
  
  .mobile-betslip-panel {
    height: 100vh;
  }
  
  .mobile-betslip-header {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .mobile-close-button {
    touch-action: manipulation;
  }
  
  :global(body.iframe-mode) .mobile-betslip-panel {
    height: 100vh !important;
    min-height: 100vh !important;
    max-height: 100vh !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    padding-bottom: 20px;
    z-index: 10001 !important;
  }
}

@media (min-width: 769px) {
  .mobile-betslip-panel {
    display: none;
  }
  
  .desktop-modal-overlay {
    padding: 8px;
  }
  
  .desktop-modal-box {
    padding: 8px;
    max-width: 1280px;
  }
}

/* Slide down animation shared with BaseModal */
.modal-slide-down-enter-active,
.modal-slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-slide-down-enter-from,
.modal-slide-down-leave-to {
  opacity: 0;
}

.modal-slide-down-enter-from .desktop-modal-box,
.modal-slide-down-leave-to .desktop-modal-box {
  transform: translateY(-50px);
}
</style>
  